'use client'

import { Check, Minus, Info } from 'lucide-react'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'
import type { MarketingPlan } from '@/config/pricing'
import { PRICING_COMPARISON_DATA } from '@/config/pricing-comparison'
import type { PlanTier } from '@/lib/billing'
import { cn } from '@/lib/utils'

export function ComparisonTableFull({
  plans,
}: {
  plans: MarketingPlan[]
  isYearly: boolean
  onCheckout: (plan: PlanTier) => void
}) {
  return (
    <TooltipProvider>
      <div className="w-full text-left">
        <div className="overflow-hidden rounded-[24px] border border-gray-200 bg-white dark:border-white/5 dark:bg-[#111]">
          {/* Header Sticky - Desktop */}
          <div className="sticky top-0 z-20 hidden md:block">
            <div className="border-b border-gray-100 bg-white/90 shadow-sm backdrop-blur-md dark:border-white/5 dark:bg-[#111]/90">
              <div className="grid grid-cols-4 gap-6 px-8 py-6">
                <div className="self-center">
                  <p className="text-[11px] font-bold uppercase tracking-widest text-gray-400">
                    Comparativa
                  </p>
                </div>
                {plans.map((plan) => (
                  <div key={plan.planTier} className="text-center">
                    <div className="flex items-center justify-center gap-2">
                      <span className="text-foreground text-sm font-bold tracking-tight">
                        {plan.name}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Categories + Rows */}
          {PRICING_COMPARISON_DATA.map((category, categoryIdx) => (
            <div
              key={category.id}
              className={cn(categoryIdx > 0 && 'border-t border-gray-100 dark:border-white/5')}
            >
              <div className="bg-gray-50/50 px-6 py-3 md:px-8 dark:bg-white/[0.02]">
                <h4 className="text-muted-foreground text-xs font-bold uppercase tracking-wider">
                  {category.label}
                </h4>
              </div>

              <div className="divide-y divide-gray-100 dark:divide-white/5">
                {category.features.map((feature) => (
                  <div
                    key={feature.id}
                    className="grid grid-cols-1 px-6 py-4 transition-colors hover:bg-gray-50/50 md:grid-cols-4 md:px-8 dark:hover:bg-white/[0.02]"
                  >
                    <div className="mb-3 flex items-center gap-2 md:mb-0 md:py-1">
                      <span
                        className={cn(
                          'text-sm font-medium',
                          feature.important
                            ? 'text-foreground font-semibold'
                            : 'text-gray-600 dark:text-gray-400'
                        )}
                      >
                        {feature.label}
                      </span>
                      {feature.helper && (
                        <Tooltip delayDuration={0}>
                          <TooltipTrigger aria-label={`Info: ${feature.label}`}>
                            <Info
                              className="h-3.5 w-3.5 text-gray-400 transition-colors hover:text-gray-600"
                              aria-hidden="true"
                            />
                          </TooltipTrigger>
                          <TooltipContent className="max-w-[220px] text-xs">
                            {feature.helper}
                          </TooltipContent>
                        </Tooltip>
                      )}
                    </div>

                    {plans.map((plan) => {
                      const value = feature.values[plan.planTier]
                      return (
                        <div
                          key={plan.planTier}
                          className="flex items-center justify-between border-t border-dashed border-gray-100 py-1 md:justify-center md:border-0 md:py-0 dark:border-white/5"
                        >
                          <span className="text-muted-foreground mr-4 text-xs font-medium md:hidden">
                            {plan.name}
                          </span>

                          {typeof value === 'boolean' ? (
                            value ? (
                              <div
                                className="flex h-5 w-5 items-center justify-center rounded-full bg-black text-white dark:bg-white dark:text-black"
                                role="img"
                                aria-label="Incluye"
                              >
                                <Check className="h-3 w-3" strokeWidth={3} aria-hidden="true" />
                              </div>
                            ) : (
                              <div
                                className="flex h-4 w-4 items-center justify-center"
                                role="img"
                                aria-label="No incluido"
                              >
                                <Minus
                                  className="h-4 w-4 text-gray-300 dark:text-gray-700"
                                  aria-hidden="true"
                                />
                              </div>
                            )
                          ) : (
                            <span
                              className={cn(
                                'text-center text-sm font-medium',
                                feature.important
                                  ? 'text-foreground'
                                  : 'text-gray-600 dark:text-gray-400'
                              )}
                            >
                              {value || '-'}
                            </span>
                          )}
                        </div>
                      )
                    })}
                  </div>
                ))}
              </div>
            </div>
          ))}

          {/* Footer note */}
          <div className="border-t border-gray-100 bg-gray-50/30 px-6 py-4 text-center text-xs text-gray-400 md:px-8 dark:border-white/5 dark:bg-white/[0.02] dark:text-gray-500">
            Los límites y características pueden ajustarse. Enterprise se define según acuerdo.
          </div>
        </div>
      </div>
    </TooltipProvider>
  )
}
